import { crrem_path_graph_type_enum } from '@predium/client-graphql';
import { DEFAULT_DISCOUNT_RATE } from '@predium/client-lookup';
import { scope_perspective_enum } from '@predium/enums';
import { createContext, PropsWithChildren, useContext, useState } from 'react';

/**
 * Display-only filter state shared by the Status Quo building analysis widgets
 * (CRREM sub-tab, scope perspectives, discount rate). Lives at this level so the
 * Detailed PDF export can read each filter's current value at submission time
 * and freeze it into the shared snapshot — the public shared view re-seeds the
 * provider from the snapshot so the rendered PDF mirrors the user's view.
 */

type CrremPathGraphType = 'co2-graph' | 'energy-graph' | 'excess-emission-cost' | 'stranding-year';

/**
 * CRREM-vs-Custom-Target-Path toggle on the Demand tab (mirrors CrremAnalysis'
 * `CustomTargetPathType`). Only meaningful when the building has a custom target
 * path. Tracked here — like the sub-chart selection — so the Detailed PDF export
 * can freeze it and the shared view re-renders the same path the user was viewing.
 *
 * `null` means "not explicitly chosen": the widget falls back to its own default
 * (custom-energy when a custom path exists, otherwise creem). Keeping it nullable
 * preserves the live-page default and only forwards a value once the user toggles.
 */
export type CrremPathsType = 'creem' | 'custom-energy';

/**
 * The widget uses kebab-case string literals; the GraphQL enum can't contain
 * hyphens, so these maps translate at the export/import boundary (mutation
 * variables out, snapshot seed in). Single source of truth for the mapping.
 */
export const crremPathGraphTypeToEnum: Record<CrremPathGraphType, crrem_path_graph_type_enum> = {
  'co2-graph': crrem_path_graph_type_enum.CO2_GRAPH,
  'energy-graph': crrem_path_graph_type_enum.ENERGY_GRAPH,
  'excess-emission-cost': crrem_path_graph_type_enum.EXCESS_EMISSION_COST,
  'stranding-year': crrem_path_graph_type_enum.STRANDING_YEAR,
};

export const crremPathGraphTypeFromEnum: Record<crrem_path_graph_type_enum, CrremPathGraphType> = {
  [crrem_path_graph_type_enum.CO2_GRAPH]: 'co2-graph',
  [crrem_path_graph_type_enum.ENERGY_GRAPH]: 'energy-graph',
  [crrem_path_graph_type_enum.EXCESS_EMISSION_COST]: 'excess-emission-cost',
  [crrem_path_graph_type_enum.STRANDING_YEAR]: 'stranding-year',
};

/**
 * Mirror the rounding the discount-rate input applies so the context default
 * matches what the widget originally rendered as its starting value.
 */
const computeDefaultDiscountRate = (): number => Math.round(DEFAULT_DISCOUNT_RATE * 100 * 10) / 10;

/**
 * CRREM sub-chart and discount rate are tracked separately per tab so switching
 * the chart (or editing the rate) on Demand doesn't bleed into Consumption and
 * vice versa. The PDF export reads the slot matching the exported tab.
 */
type StatusQuoViewFiltersValue = {
  demandCrremPathGraphType: CrremPathGraphType;
  setDemandCrremPathGraphType: (value: CrremPathGraphType) => void;
  demandCrremPathsType: CrremPathsType | null;
  setDemandCrremPathsType: (value: CrremPathsType | null) => void;
  consumptionCrremPathGraphType: CrremPathGraphType;
  setConsumptionCrremPathGraphType: (value: CrremPathGraphType) => void;
  demandScopePerspective: scope_perspective_enum;
  setDemandScopePerspective: (value: scope_perspective_enum) => void;
  demandDiscountRate: number | null;
  setDemandDiscountRate: (value: number | null) => void;
  consumptionDiscountRate: number | null;
  setConsumptionDiscountRate: (value: number | null) => void;
  consumptionScopePerspective: scope_perspective_enum;
  setConsumptionScopePerspective: (value: scope_perspective_enum) => void;
};

const noop = () => {};

const defaultContextValue: StatusQuoViewFiltersValue = {
  demandCrremPathGraphType: 'co2-graph',
  setDemandCrremPathGraphType: noop,
  demandCrremPathsType: null,
  setDemandCrremPathsType: noop,
  consumptionCrremPathGraphType: 'co2-graph',
  setConsumptionCrremPathGraphType: noop,
  demandScopePerspective: scope_perspective_enum.WHOLE_BUILDING,
  setDemandScopePerspective: noop,
  demandDiscountRate: computeDefaultDiscountRate(),
  setDemandDiscountRate: noop,
  consumptionDiscountRate: computeDefaultDiscountRate(),
  setConsumptionDiscountRate: noop,
  consumptionScopePerspective: scope_perspective_enum.WHOLE_BUILDING,
  setConsumptionScopePerspective: noop,
};

const StatusQuoViewFiltersContext = createContext<StatusQuoViewFiltersValue>(defaultContextValue);

export type StatusQuoViewFiltersInitialValues = Partial<{
  demandCrremPathGraphType: CrremPathGraphType;
  demandCrremPathsType: CrremPathsType | null;
  consumptionCrremPathGraphType: CrremPathGraphType;
  demandScopePerspective: scope_perspective_enum;
  demandDiscountRate: number | null;
  consumptionDiscountRate: number | null;
  consumptionScopePerspective: scope_perspective_enum;
}>;

type ProviderProps = PropsWithChildren<{
  /**
   * Optional seed values. The shared/PDF flow passes the snapshot's frozen
   * filter selections here so the rendered widgets match what the user saw
   * when they triggered the export.
   */
  initialValues?: StatusQuoViewFiltersInitialValues;
}>;

export function StatusQuoViewFiltersProvider({ initialValues, children }: ProviderProps) {
  const [demandCrremPathGraphType, setDemandCrremPathGraphType] = useState<CrremPathGraphType>(
    initialValues?.demandCrremPathGraphType ?? 'co2-graph',
  );
  const [demandCrremPathsType, setDemandCrremPathsType] = useState<CrremPathsType | null>(
    initialValues?.demandCrremPathsType ?? null,
  );
  const [consumptionCrremPathGraphType, setConsumptionCrremPathGraphType] = useState<CrremPathGraphType>(
    initialValues?.consumptionCrremPathGraphType ?? 'co2-graph',
  );
  const [demandScopePerspective, setDemandScopePerspective] = useState<scope_perspective_enum>(
    initialValues?.demandScopePerspective ?? scope_perspective_enum.WHOLE_BUILDING,
  );
  const [demandDiscountRate, setDemandDiscountRate] = useState<number | null>(
    initialValues?.demandDiscountRate ?? computeDefaultDiscountRate(),
  );
  const [consumptionDiscountRate, setConsumptionDiscountRate] = useState<number | null>(
    initialValues?.consumptionDiscountRate ?? computeDefaultDiscountRate(),
  );
  const [consumptionScopePerspective, setConsumptionScopePerspective] = useState<scope_perspective_enum>(
    initialValues?.consumptionScopePerspective ?? scope_perspective_enum.WHOLE_BUILDING,
  );

  return (
    <StatusQuoViewFiltersContext.Provider
      value={{
        demandCrremPathGraphType,
        setDemandCrremPathGraphType,
        demandCrremPathsType,
        setDemandCrremPathsType,
        consumptionCrremPathGraphType,
        setConsumptionCrremPathGraphType,
        demandScopePerspective,
        setDemandScopePerspective,
        demandDiscountRate,
        setDemandDiscountRate,
        consumptionDiscountRate,
        setConsumptionDiscountRate,
        consumptionScopePerspective,
        setConsumptionScopePerspective,
      }}
    >
      {children}
    </StatusQuoViewFiltersContext.Provider>
  );
}

export function useStatusQuoViewFilters() {
  return useContext(StatusQuoViewFiltersContext);
}
